/**
 * Lyra size wizard.
 *
 * Drives the guided sizing drawer: path selection (measurements + fit, or
 * customer match), recommendation calculation, and PDP variant selection.
 *
 * Notes for the next person:
 * - The drawer reads its config from a #size-wizard-config <script type="application/json">
 *   tag, rendered server-side by snippets/size-wizard.liquid.
 * - Sizing data lives in snippets/size-wizard-data.liquid (Phase 2a launch).
 *   Phase 2b may source the same JSON shape from Shopify metaobjects.
 * - customerGalleryEnabled in config (snippets/size-wizard.liquid) gates Path B
 *   and the post-recommendation gallery until real UGC images exist.
 * - One display unit (cm or in) is shared by the measurement inputs and size
 *   chart table; both toggles stay in sync.
 * - Table cells show Kiwi-style single values (midpoint of each cm range,
 *   rounded). Ranges in data are only used for recommendations.
 * - Static columns (e.g. garment lengths) convert to the active display unit.
 * - The recommendation algorithm is intentionally simple: midpoint distance
 *   over a small number of measurements, then a fit offset, then a confidence
 *   score driven by how many ranges the customer falls inside. A more
 *   sophisticated model is a Phase 2 concern.
 */

(function () {
  'use strict';

  const CM_TO_IN = 0.393701;
  const REGION_NAMES = { US: 'US', EU: 'Europe' };
  const MEASURE_BOUNDS = {
    bust: { cm: [40, 200], in: [16, 79] },
    waist: { cm: [40, 200], in: [16, 79] },
    hips: { cm: [40, 200], in: [16, 79] },
    chest: { cm: [80, 140], in: [32, 55] }
  };
  /** Body measurements with how-to-measure copy and illustration assets. */
  const SUPPORTED_MEASURE_GUIDES = ['chest', 'bust', 'waist', 'hips'];
  const FIT_LABELS = {
    snug: 'a snug, secure',
    'true': 'a true-to-size',
    relaxed: 'a relaxed'
  };
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  function parseConfig() {
    const el = document.getElementById('size-wizard-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[size-wizard] Invalid config JSON', e);
      return null;
    }
  }

  function findBaseSize(measurements, chart, relevantMeasurements) {
    let bestSize = null;
    let bestScore = Infinity;

    for (const size of chart.sizes) {
      const ranges = chart.ranges[size];
      if (!ranges) continue;

      let score = 0;
      for (const m of relevantMeasurements) {
        const range = ranges[m];
        if (!range) continue;
        const mid = (range[0] + range[1]) / 2;
        score += Math.abs(measurements[m] - mid);
      }
      if (score < bestScore) {
        bestScore = score;
        bestSize = size;
      }
    }
    return bestSize;
  }

  function applyFitOffset(baseSize, fitOption, measurements, chart, relevantMeasurements) {
    if (!fitOption || !fitOption.offset) return baseSize;
    const sizeIndex = chart.sizes.indexOf(baseSize);
    if (sizeIndex < 0) return baseSize;

    const clamp = (i) => Math.min(Math.max(i, 0), chart.sizes.length - 1);

    if (fitOption.condition === 'always') {
      return chart.sizes[clamp(sizeIndex + fitOption.offset)];
    }

    if (fitOption.condition === 'upper_half' && fitOption.offset < 0) {
      const primary = relevantMeasurements[0];
      const range = chart.ranges[baseSize][primary];
      const mid = (range[0] + range[1]) / 2;
      if (measurements[primary] >= mid) return baseSize;
      return chart.sizes[clamp(sizeIndex + fitOption.offset)];
    }

    return chart.sizes[clamp(sizeIndex + fitOption.offset)];
  }

  function confidenceFor(measurements, size, chart, relevantMeasurements) {
    const ranges = chart.ranges[size];
    if (!ranges) return 60;

    const matches = relevantMeasurements.filter((m) => {
      const [min, max] = ranges[m];
      return measurements[m] >= min && measurements[m] <= max;
    }).length;

    if (matches === relevantMeasurements.length) return 95;
    if (matches >= relevantMeasurements.length - 1) return 80;
    return 65;
  }

  /**
   * Detects whether the customer is sitting cleanly in one size range
   * versus straddling two. Used to surface the "between two sizes" hint
   * only when it's actually true.
   */
  function isBetweenSizes(measurements, size, chart, relevantMeasurements) {
    const ranges = chart.ranges[size];
    if (!ranges) return false;

    return relevantMeasurements.some((m) => {
      const [min, max] = ranges[m];
      const value = measurements[m];
      return value < min || value > max;
    });
  }

  function setText(node, text) {
    if (node) node.textContent = text;
  }

  function format(template, replacements) {
    let out = template || '';
    for (const key of Object.keys(replacements)) {
      out = out.replace(new RegExp('\\{\\{\\s*' + key + '\\s*\\}\\}', 'g'), replacements[key]);
    }
    return out;
  }

  class SizeWizard {
    constructor(config) {
      this.config = config;
      this.chartData = config.chart;
      const profileId = String(config.sizingProfile || 'default').trim();
      const chartId =
        config.chartId ||
        this.chartData.profiles?.[profileId]?.chartId ||
        (this.chartData.charts?.[profileId] ? profileId : null) ||
        'swim';

      this.chart = this.chartData.charts?.[chartId] || this.chartData.charts.swim;
      this.profile =
        this.chartData.profiles?.[profileId] || this.chartData.profiles?.default;

      if (
        profileId !== 'default' &&
        chartId !== 'swim' &&
        this.profile === this.chartData.profiles?.default &&
        !this.chartData.profiles?.[profileId]
      ) {
        this.profile = {
          chartId,
          measurements: (this.chart.tableColumns || [])
            .filter((col) => col.type === 'range')
            .map((col) => col.key),
          fitNote: this.profile?.fitNote,
          fitOptions: this.profile?.fitOptions
        };
      }

      this.strings = config.strings || {};
      this.customerGalleryEnabled = config.customerGalleryEnabled === true;
      // Chart-only mode when explicitly flagged, or when the chart has no
      // body-measurement (range) columns to recommend against — without it the
      // recommendation logic would confidently return the smallest size.
      this.chartOnly =
        this.profile.chartOnly === true ||
        !(this.chart.tableColumns || []).some((col) => col.type === 'range');

      this.drawer = document.getElementById('size-wizard');
      this.overlay = document.getElementById('size-wizard-overlay');
      if (!this.drawer || !this.overlay) return;

      this.progressBar = this.drawer.querySelector('[data-size-wizard-progress]');
      this.steps = Array.from(this.drawer.querySelectorAll('[data-step]'));
      this.fitOptionsEl = this.drawer.querySelector('[data-fit-options]');
      this.fitNoteEl = this.drawer.querySelector('[data-fit-note]');
      this.fitNoteText = this.drawer.querySelector('[data-fit-note-text]');
      this.matchGalleryEl = this.drawer.querySelector('[data-match-gallery]');
      this.resultGalleryGrid = this.drawer.querySelector('[data-result-gallery-grid]');
      this.resultBackBtn = this.drawer.querySelector('[data-result-back]');
      this.betweenSizesNote = this.drawer.querySelector('[data-between-sizes-note]');

      this.currentStep = 'choose';
      this.currentUnit = 'cm';
      this.currentChartUnit = 'cm';
      this.currentRegion = 'AU';
      this.selectedFit = null;
      this.recommendedSize = null;
      this.recommendationSource = 'measure';
      this.lastMeasurements = null;
      this._focusTrapHandler = null;
      this._previousFocus = null;
      this._closeTimer = null;

      this._bind();
      if (this.chartOnly) this._configureChartOnlyMode();
      this._syncMeasurementFields();
      this._syncMeasureFieldGuides();
      this._syncChartGuides();
      this._renderFitOptions();
      this._renderMatchGallery();
      this._renderResultGallery();
      this._updateChartProductNote();
      this._adjustInputBounds(this.currentUnit);
      this.renderSizeTable('cm', 'AU');
    }

    _configureChartOnlyMode() {
      const measurePath = this.drawer.querySelector('[data-path="measurements"]');
      measurePath?.setAttribute('hidden', '');
      measurePath?.setAttribute('aria-hidden', 'true');
      measurePath?.setAttribute('tabindex', '-1');

      const intro = this.drawer.querySelector('.size-wizard__intro');
      if (intro) {
        intro.textContent =
          'Garment lengths by size — open the size chart, then choose your size on the product page.';
      }
    }

    _resolveProductStatic(size) {
      const byProduct = this.chart.productLengthCm;
      if (byProduct) {
        const handle = this.config.productHandle;
        let row = handle ? byProduct[handle] : null;
        if (!row) {
          const title = (this.config.productTitle || '').toLowerCase();
          const key = Object.keys(byProduct).find((h) => {
            const label = h.replace(/-/g, ' ');
            return title.includes(label) || label.includes(title);
          });
          row = key ? byProduct[key] : null;
        }
        if (row && row[size] != null) {
          return { length_cm: row[size] };
        }
      }
      return this.chart.static?.[size] || {};
    }

    /**
     * Range columns from this product's size chart that also have a
     * how-to-measure guide (copy + illustration).
     */
    _getGuideMeasurements() {
      const columns = this.chart?.tableColumns || [];
      return columns
        .filter((col) => col.type === 'range')
        .map((col) => col.key)
        .filter((key) => SUPPORTED_MEASURE_GUIDES.includes(key));
    }

    _getRangeColumnLabels() {
      const labels = {};
      (this.chart?.tableColumns || []).forEach((col) => {
        if (col.type === 'range' && col.label) labels[col.key] = col.label;
      });
      return labels;
    }

    _syncMeasurementFields() {
      const relevant = this.profile?.measurements || ['bust', 'waist', 'hips'];
      this.drawer.querySelectorAll('[data-measure-field]').forEach((field) => {
        const measure = field.getAttribute('data-measure-field');
        field.hidden = !relevant.includes(measure);
      });
      const columnLabels = this._getRangeColumnLabels();
      this.drawer.querySelectorAll('[data-measure-input]').forEach((input) => {
        const measure = input.getAttribute('data-measure-input');
        const label = this.drawer.querySelector(`label[for="${input.id}"]`);
        if (label && columnLabels[measure]) label.textContent = columnLabels[measure];
      });
    }

    _syncMeasureFieldGuides() {
      const relevant = this._getGuideMeasurements();
      this.drawer.querySelectorAll('[data-measure-toggle]').forEach((btn) => {
        const measure = btn.getAttribute('data-measure-toggle');
        const show = relevant.includes(measure);
        btn.hidden = !show;
        btn.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (!show) {
          btn.setAttribute('aria-expanded', 'false');
          const guide = this.drawer.querySelector(`[data-measure-guide="${measure}"]`);
          if (guide) guide.hidden = true;
        }
      });
    }

    _bind() {
      document.querySelectorAll('[data-size-wizard-open], .size-wizard-trigger').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      this.drawer.querySelectorAll('[data-size-wizard-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      this.overlay.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('size-wizard--open')) {
          this.close();
        }
      });

      this.drawer.querySelectorAll('[data-path]').forEach((card) => {
        card.addEventListener('click', () => {
          const path = card.getAttribute('data-path');
          if (path === 'measurements') this.goToStep('measurements');
          if (path === 'match' && this.customerGalleryEnabled) this.goToStep('match');
        });
      });

      this.drawer.querySelectorAll('[data-goto]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          // Result-step back is dynamic: the JS rewrites its data-goto target
          // before the click reaches us; we read whatever is current.
          this.goToStep(btn.getAttribute('data-goto'));
        });
      });

      this.drawer.querySelectorAll('[data-unit]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const group = btn.closest('.size-wizard__unit-toggle');
          if (!group || group.querySelector('[data-chart-unit]')) return;
          this._setDisplayUnit(btn.getAttribute('data-unit'));
        });
      });

      this.drawer.querySelectorAll('[data-measure-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this._toggleAccordion(btn, '[data-measure-toggle]', '[data-measure-guide]', 'data-measure-guide', btn.getAttribute('data-measure-toggle'));
        });
      });

      const measureNext = this.drawer.querySelector('[data-measure-next]');
      const fitNext = this.drawer.querySelector('[data-fit-next]');
      const selectSize = this.drawer.querySelector('[data-select-size]');

      this.drawer.querySelectorAll('[data-measure-input]').forEach((input) => {
        input.addEventListener('input', () => this._checkMeasurements(measureNext));
      });

      measureNext?.addEventListener('click', () => this.goToStep('fit'));
      fitNext?.addEventListener('click', () => {
        this._calculateRecommendation();
        this.goToStep('result');
      });
      selectSize?.addEventListener('click', () => this._applySizeToPdp());

      this.drawer.querySelectorAll('[data-chart-unit]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this._setDisplayUnit(btn.getAttribute('data-chart-unit'));
        });
      });

      const regionSelect = this.drawer.querySelector('[data-chart-region]');
      regionSelect?.addEventListener('change', () => {
        this.currentRegion = regionSelect.value;
        this.renderSizeTable(this.currentChartUnit, this.currentRegion);
      });

      this.drawer.querySelectorAll('[data-chart-measure-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this._toggleAccordion(btn, '[data-chart-measure-toggle]', '[data-chart-measure-panel]', 'data-chart-measure-panel', btn.getAttribute('data-chart-measure-toggle'));
        });
      });
    }

    _toggleAccordion(btn, toggleSelector, panelSelector, panelAttr, key) {
      const panel = this.drawer.querySelector(`[${panelAttr}="${key}"]`);
      const isOpen = btn.getAttribute('aria-expanded') === 'true';

      this.drawer.querySelectorAll(toggleSelector).forEach((b) => {
        b.setAttribute('aria-expanded', 'false');
      });
      this.drawer.querySelectorAll(panelSelector).forEach((p) => {
        p.hidden = true;
      });

      if (!isOpen && panel) {
        panel.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
      }
    }

    _adjustInputBounds(unit) {
      const step = unit === 'in' ? '0.25' : '0.5';
      this.drawer.querySelectorAll('[data-measure-input]').forEach((input) => {
        const key = input.getAttribute('data-measure-input');
        const bounds = MEASURE_BOUNDS[key]?.[unit] || (unit === 'in' ? [16, 80] : [40, 200]);
        input.min = String(bounds[0]);
        input.max = String(bounds[1]);
        input.step = step;
      });
    }

    _renderFitOptions() {
      if (!this.fitOptionsEl || !this.profile?.fitOptions) return;
      this.fitOptionsEl.innerHTML = '';

      this.profile.fitOptions.forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'size-wizard__fit-card';
        btn.dataset.fit = opt.id;
        btn.setAttribute('aria-pressed', 'false');

        const name = document.createElement('span');
        name.className = 'size-wizard__fit-name';
        name.textContent = opt.label;
        btn.appendChild(name);

        const desc = document.createElement('span');
        desc.className = 'size-wizard__fit-desc';
        desc.textContent = opt.desc;
        btn.appendChild(desc);

        btn.addEventListener('click', () => {
          this.fitOptionsEl.querySelectorAll('.size-wizard__fit-card').forEach((c) => {
            c.classList.remove('size-wizard__fit-card--selected');
            c.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('size-wizard__fit-card--selected');
          btn.setAttribute('aria-pressed', 'true');
          this.selectedFit = opt.id;
          const fitNext = this.drawer.querySelector('[data-fit-next]');
          if (fitNext) fitNext.disabled = false;
        });
        this.fitOptionsEl.appendChild(btn);
      });
    }

    _renderMatchGallery() {
      if (!this.customerGalleryEnabled || !this.matchGalleryEl) return;
      this.matchGalleryEl.innerHTML = '';

      const items = this.chart.matchGallery || [];
      if (!items.length) {
        const empty = document.createElement('p');
        empty.className = 'size-wizard__match-empty';
        empty.textContent =
          this.strings.matchGalleryEmpty ||
          'Customer photos for this style are coming soon. Try the measurements path for a confident match.';
        this.matchGalleryEl.appendChild(empty);
        return;
      }

      items.forEach((item) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'size-wizard__match-card';
        card.dataset.matchSize = item.size;
        card.setAttribute('aria-pressed', 'false');

        const photo = document.createElement('div');
        photo.className = 'size-wizard__match-photo';
        const placeholder = document.createElement('div');
        placeholder.className = 'size-wizard__match-placeholder';
        placeholder.textContent = 'Photo';
        photo.appendChild(placeholder);
        card.appendChild(photo);

        const info = document.createElement('div');
        info.className = 'size-wizard__match-info';

        const size = document.createElement('span');
        size.className = 'size-wizard__match-size';
        size.textContent = `Size ${item.size}`;
        info.appendChild(size);

        const height = document.createElement('span');
        height.className = 'size-wizard__match-height';
        height.textContent = item.height;
        info.appendChild(height);

        if (item.note) {
          const note = document.createElement('span');
          note.className = 'size-wizard__match-note';
          note.textContent = `\u201C${item.note}\u201D`;
          info.appendChild(note);
        }

        card.appendChild(info);
        card.addEventListener('click', () => {
          this.matchGalleryEl.querySelectorAll('.size-wizard__match-card').forEach((c) => {
            c.classList.remove('size-wizard__match-card--selected');
            c.setAttribute('aria-pressed', 'false');
          });
          card.classList.add('size-wizard__match-card--selected');
          card.setAttribute('aria-pressed', 'true');
          this.recommendedSize = item.size;
          this.recommendationSource = 'match';
          this._showMatchResult(item.size);

          const delay = prefersReducedMotion() ? 0 : 240;
          window.setTimeout(() => this.goToStep('result'), delay);
        });
        this.matchGalleryEl.appendChild(card);
      });
    }

    _renderResultGallery() {
      if (!this.customerGalleryEnabled || !this.resultGalleryGrid) return;
      const notes = this.chart.resultGalleryNotes || [];
      this.resultGalleryGrid.innerHTML = '';

      notes.forEach((note) => {
        const card = document.createElement('div');
        card.className = 'size-wizard__result-gallery-card';

        const photo = document.createElement('div');
        photo.className = 'size-wizard__result-gallery-photo';
        const placeholder = document.createElement('div');
        placeholder.className = 'size-wizard__match-placeholder';
        placeholder.textContent = 'Photo';
        photo.appendChild(placeholder);
        card.appendChild(photo);

        if (note) {
          const noteEl = document.createElement('span');
          noteEl.className = 'size-wizard__result-gallery-note';
          noteEl.textContent = `\u201C${note}\u201D`;
          card.appendChild(noteEl);
        }

        this.resultGalleryGrid.appendChild(card);
      });
    }

    _updateChartProductNote() {
      const wrap = this.drawer.querySelector('[data-chart-product-note]');
      const text = this.drawer.querySelector('[data-chart-product-note-text]');
      if (!wrap || !text || !this.profile?.fitNote) return;
      text.textContent = this.profile.fitNote;
      wrap.hidden = false;
    }

    open() {
      if (this._closeTimer) {
        window.clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }
      this._previousFocus = document.activeElement;
      this.overlay.hidden = false;
      this.drawer.hidden = false;
      this.drawer.classList.remove('size-wizard--closing');

      requestAnimationFrame(() => {
        this.overlay.classList.add('size-wizard__overlay--visible');
        this.drawer.classList.add('size-wizard--open');
        this.drawer.setAttribute('aria-hidden', 'false');
      });
      document.body.style.overflow = 'hidden';
      this._enableFocusTrap();
      const closeBtn = this.drawer.querySelector('[data-size-wizard-close]');
      closeBtn?.focus();
    }

    close() {
      this.overlay.classList.remove('size-wizard__overlay--visible');
      this.drawer.classList.remove('size-wizard--open');
      this.drawer.classList.add('size-wizard--closing');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this._disableFocusTrap();

      const delay = prefersReducedMotion() ? 0 : 260;
      this._closeTimer = window.setTimeout(() => {
        this.overlay.hidden = true;
        this.drawer.hidden = true;
        this.drawer.classList.remove('size-wizard--closing');
        this._closeTimer = null;
      }, delay);

      if (this._previousFocus && typeof this._previousFocus.focus === 'function') {
        this._previousFocus.focus();
      }
    }

    _enableFocusTrap() {
      const focusable = () =>
        Array.from(
          this.drawer.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => el.offsetParent !== null);

      this._focusTrapHandler = (e) => {
        if (e.key !== 'Tab' || !this.drawer.classList.contains('size-wizard--open')) return;
        const nodes = focusable();
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', this._focusTrapHandler);
    }

    _disableFocusTrap() {
      if (this._focusTrapHandler) {
        document.removeEventListener('keydown', this._focusTrapHandler);
        this._focusTrapHandler = null;
      }
    }

    goToStep(stepName) {
      if (stepName === 'match' && !this.customerGalleryEnabled) {
        stepName = 'choose';
      }

      this.steps.forEach((step) => {
        const active = step.getAttribute('data-step') === stepName;
        step.classList.toggle('size-wizard__step--active', active);
        step.hidden = !active;
      });
      this.currentStep = stepName;
      this._updateProgress();

      if (stepName === 'fit' && this.fitNoteEl && this.profile?.fitNote) {
        this.fitNoteEl.hidden = false;
        if (this.fitNoteText) this.fitNoteText.textContent = this.profile.fitNote;
      }

      // Result back routes to whichever path produced the recommendation.
      if (stepName === 'result' && this.resultBackBtn) {
        const backStep =
          this.recommendationSource === 'match' && this.customerGalleryEnabled
            ? 'match'
            : 'fit';
        this.resultBackBtn.setAttribute('data-goto', backStep);
      }

      // Autofocus the first measurement on entering the measurements step.
      if (stepName === 'measurements') {
        const first = this.drawer.querySelector('[data-measure-input]');
        if (first && document.activeElement !== first) {
          window.requestAnimationFrame(() => first.focus({ preventScroll: true }));
        }
      }

      // Scroll the active step to the top so steps don't carry over scroll position.
      const active = this.drawer.querySelector('.size-wizard__step--active');
      if (active) active.scrollTop = 0;
    }

    _updateProgress() {
      const map = {
        choose: 0,
        measurements: 33,
        fit: 66,
        match: 50,
        result: 100,
        sizechart: 0
      };
      const pct = map[this.currentStep] || 0;
      if (this.progressBar) {
        this.progressBar.style.width = `${pct}%`;
      }
      const wrap = this.drawer.querySelector('.size-wizard__progress');
      if (wrap) {
        wrap.classList.toggle('size-wizard__progress--idle', pct === 0);
      }
    }

    _setDisplayUnit(newUnit) {
      if (newUnit !== 'cm' && newUnit !== 'in') return;
      if (newUnit === this.currentUnit) {
        this.currentChartUnit = newUnit;
        this.renderSizeTable(this.currentChartUnit, this.currentRegion);
        return;
      }

      this._convertMeasureInputs(this.currentUnit, newUnit);
      this.currentUnit = newUnit;
      this.currentChartUnit = newUnit;
      this._syncUnitToggleUI(newUnit);
      this.drawer.querySelectorAll('[data-measure-unit]').forEach((el) => {
        el.textContent = newUnit === 'cm' ? 'cm' : 'in';
      });
      this._adjustInputBounds(newUnit);
      this.renderSizeTable(this.currentChartUnit, this.currentRegion);
    }

    _syncUnitToggleUI(unit) {
      this.drawer.querySelectorAll('[data-unit], [data-chart-unit]').forEach((btn) => {
        const btnUnit = btn.getAttribute('data-unit') || btn.getAttribute('data-chart-unit');
        const active = btnUnit === unit;
        btn.classList.toggle('size-wizard__unit-btn--active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    _formatStaticForTable(val, storedUnit, displayUnit) {
      if (val == null) return null;
      const num = Number(val);
      if (Number.isNaN(num)) return null;

      let cm;
      if (storedUnit === 'cm') cm = num;
      else if (storedUnit === 'in') cm = num / CM_TO_IN;
      else return String(num);

      if (displayUnit === 'in') {
        return String(Math.round(cm * CM_TO_IN));
      }
      return String(Math.round(cm));
    }

    _convertMeasureInputs(from, to) {
      this.drawer.querySelectorAll('[data-measure-input]').forEach((input) => {
        if (!input.value) return;
        const val = parseFloat(input.value);
        if (Number.isNaN(val)) return;
        if (to === 'in') {
          input.value = String(Math.round(val * CM_TO_IN * 4) / 4);
        } else {
          input.value = String(Math.round(val / CM_TO_IN * 2) / 2);
        }
      });
    }

    _checkMeasurements(btn) {
      if (!btn) return;
      const relevant = this.profile?.measurements || ['bust', 'waist', 'hips'];
      const filled = relevant.every((m) => {
        const input = this.drawer.querySelector(`[data-measure-input="${m}"]`);
        if (!input || input.value === '') return false;
        const val = parseFloat(input.value);
        return !Number.isNaN(val) && val > 0;
      });
      btn.disabled = !filled;
    }

    _getMeasurementsCm() {
      const relevant = this.profile.measurements || ['bust', 'waist', 'hips'];
      const out = {};
      relevant.forEach((m) => {
        const input = this.drawer.querySelector(`[data-measure-input="${m}"]`);
        let val = parseFloat(input?.value);
        if (Number.isNaN(val)) {
          out[m] = null;
          return;
        }
        if (this.currentUnit === 'in') val = val / CM_TO_IN;
        out[m] = val;
      });
      return out;
    }

    _calculateRecommendation() {
      const measurements = this._getMeasurementsCm();
      const relevant = this.profile.measurements;

      if (relevant.some((m) => measurements[m] == null)) {
        this.recommendedSize = this.chart.sizes[Math.floor(this.chart.sizes.length / 2)];
        this.recommendationSource = 'measure';
        this._showMeasureResult(this.recommendedSize, 60, this.selectedFit, measurements);
        return;
      }

      const fitOption =
        this.profile.fitOptions.find((o) => o.id === this.selectedFit) ||
        this.profile.fitOptions[1];

      let size = findBaseSize(measurements, this.chart, relevant);
      if (!size) size = this.chart.sizes[Math.floor(this.chart.sizes.length / 2)];
      size = applyFitOffset(size, fitOption, measurements, this.chart, relevant);
      const confidence = confidenceFor(measurements, size, this.chart, relevant);

      this.recommendedSize = size;
      this.recommendationSource = 'measure';
      this.lastMeasurements = measurements;
      this._showMeasureResult(size, confidence, this.selectedFit, measurements);
    }

    _showMeasureResult(size, confidence, fitId, measurements) {
      const between = measurements
        ? isBetweenSizes(measurements, size, this.chart, this.profile.measurements)
        : false;
      this._updateResultUI({
        size,
        confidence,
        source: 'measure',
        fitId,
        between
      });
    }

    _showMatchResult(size) {
      this._updateResultUI({
        size,
        confidence: 75,
        source: 'match',
        fitId: null,
        between: false
      });
    }

    _updateResultUI({ size, confidence, source, fitId, between }) {
      setText(this.drawer.querySelector('[data-result-size]'), size);
      setText(this.drawer.querySelector('[data-select-size-text]'), size);

      const galleryTitle = this.drawer.querySelector('[data-result-gallery-title]');
      if (galleryTitle) {
        galleryTitle.textContent = format(this.strings.galleryTitle, { size });
      }

      const confidenceWrap = this.drawer.querySelector('[data-confidence]');
      const confidenceLabel = this.drawer.querySelector('[data-confidence-label]');
      if (confidenceWrap) {
        confidenceWrap.classList.remove('size-wizard__confidence--high', 'size-wizard__confidence--low');
        if (source === 'match') {
          // Quiet brand-neutral state for the match path.
          if (confidenceLabel) confidenceLabel.textContent = this.strings.matchConfidence || 'Based on your match';
        } else if (confidence >= 90) {
          confidenceWrap.classList.add('size-wizard__confidence--high');
          if (confidenceLabel) confidenceLabel.textContent = this.strings.highConfidence || 'High match';
        } else if (confidence >= 75) {
          if (confidenceLabel) confidenceLabel.textContent = this.strings.goodMatch || 'Good match';
        } else {
          confidenceWrap.classList.add('size-wizard__confidence--low');
          if (confidenceLabel) confidenceLabel.textContent = this.strings.approximateMatch || 'Close match';
        }
      }

      const note = this.drawer.querySelector('[data-result-note]');
      if (note) {
        if (source === 'match') {
          note.textContent = format(this.strings.resultNoteMatch, { size });
        } else {
          const fitText = FIT_LABELS[fitId] || 'a comfortable';
          const template = confidence < 90 && this.strings.resultNoteMeasureLow
            ? this.strings.resultNoteMeasureLow
            : this.strings.resultNoteMeasure;
          note.textContent = format(template, { size, fit: fitText });
        }
      }

      // The "between two sizes" hint should only appear when the customer
      // actually straddles a range or recommendation confidence is low.
      if (this.betweenSizesNote) {
        const showHint = source === 'measure' && (between || confidence < 80);
        this.betweenSizesNote.hidden = !showHint;
      }
    }

    renderSizeTable(unit, region) {
      const tbody = this.drawer.querySelector('[data-size-table-body]');
      const theadRow = this.drawer.querySelector('[data-size-table-head]');
      const regionWrap = this.drawer.querySelector('[data-chart-region-wrap]');
      if (!tbody || !theadRow) return;

      const columns = this.chart.tableColumns || [
        { key: 'bust', label: 'Bust', type: 'range' },
        { key: 'waist', label: 'Waist', type: 'range' },
        { key: 'hips', label: 'Hips', type: 'range' }
      ];
      const sizes = this.chart.sizes || [];
      const hasRegions = Boolean(this.chart.regionLabels);
      const showConversion = hasRegions && region !== 'AU';
      const labels = hasRegions
        ? this.chart.regionLabels[region] || this.chart.regionLabels.AU
        : [];

      if (regionWrap) regionWrap.hidden = !hasRegions;

      const headParts = ['<th scope="col">Size</th>'];
      if (showConversion) {
        headParts.push(`<th scope="col">${REGION_NAMES[region]}</th>`);
      }
      columns.forEach((col) => {
        headParts.push(`<th scope="col">${col.label}</th>`);
      });
      theadRow.innerHTML = headParts.join('');

      // Table shows Kiwi-style single values; ranges stay internal for recommendations.
      const fmtTableMeasure = (range) => {
        const midCm = (range[0] + range[1]) / 2;
        if (unit === 'in') {
          return String(Math.round(midCm * CM_TO_IN));
        }
        return String(Math.round(midCm));
      };

      tbody.innerHTML = sizes
        .map((size, i) => {
          const r = this.chart.ranges?.[size];
          const staticRow = this._resolveProductStatic(size);
          if (!r && !Object.keys(staticRow).length) return '';

          const conversion = showConversion
            ? `<td class="size-wizard__table-conversion">${labels[i] || ''}</td>`
            : '';

          const cells = columns
            .map((col) => {
              if (col.type === 'static') {
                const display = this._formatStaticForTable(
                  staticRow[col.key],
                  col.unit,
                  unit
                );
                if (display == null) return '<td>—</td>';
                return `<td>${display}</td>`;
              }
              const range = r?.[col.key];
              if (!range) return '<td>—</td>';
              return `<td>${fmtTableMeasure(range)}</td>`;
            })
            .join('');

          return `<tr>
          <td><strong>${size}</strong></td>
          ${conversion}
          ${cells}
        </tr>`;
        })
        .join('');
    }

    _syncChartGuides() {
      const relevant = this._getGuideMeasurements();
      const columnLabels = this._getRangeColumnLabels();
      this.drawer.querySelectorAll('[data-chart-guide-item]').forEach((item) => {
        const measure = item.getAttribute('data-chart-guide-item');
        const show = relevant.includes(measure);
        item.hidden = !show;
        if (show) {
          const labelSpan = item.querySelector('.size-wizard__chart-guide-toggle span');
          if (labelSpan && columnLabels[measure]) labelSpan.textContent = columnLabels[measure];
        }
      });
      const guidesBlock = this.drawer.querySelector('[data-chart-guides]');
      if (guidesBlock) guidesBlock.hidden = relevant.length === 0;
    }

    _applySizeToPdp() {
      if (!this.recommendedSize) return;

      const form = document.getElementById(this.config.formId);
      const variantPicker = document.querySelector(
        `variant-picker[form="${CSS.escape(this.config.formId)}"]`
      );

      const optionName = `option${this.config.sizeOptionPosition}`;
      const escaped = CSS.escape(this.recommendedSize);
      const input =
        form?.querySelector(`input[type="radio"][name="${optionName}"][value="${escaped}"]`) ||
        document.querySelector(`input[type="radio"][name="${optionName}"][value="${escaped}"][form="${this.config.formId}"]`);

      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        const label = input.id
          ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`)
          : null;
        label?.classList.add('size-wizard-recommended');
      } else if (variantPicker && typeof variantPicker.selectVariant === 'function') {
        const variant = variantPicker.product?.variants?.find((v) =>
          v.options.includes(this.recommendedSize)
        );
        if (variant) variantPicker.selectVariant(variant.id);
      }

      this.close();
    }
  }

  function init() {
    const config = parseConfig();
    if (!config) return;
    new SizeWizard(config);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
